import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, ListChecks, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppAccountClient, type AppAccountDTO } from "../../api";
import { useAuth } from "../../auth";
import { columns as defineColumns, DataTable, type RowEditorConfig } from "../../table";
import { Badge, Button, Dialog, Field, Input, useConfirm } from "../../ui";
import { PageHeader } from "../PageHeader";
import { MethodPickerDialog } from "./MethodPickerDialog";
import { SecretOnceDialog } from "./SecretOnceDialog";

/**
 * App-account administration: the machine callers that authenticate with a
 * static `dapp_*` key rather than a login.
 *
 * Three things here are not plain CRUD — issuing a key, rotating it, and
 * editing the endpoint allowlist — because the plaintext key exists only in the
 * response that creates it.
 */
export function AppAccountsPage() {
  const { t } = useTranslation();
  const { http, account } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  // Mirrors the Vaadin view's @RolesAllowed("ADMIN"). These keys are
  // credentials; listing them is not something a USER account should reach.
  const allowed = account?.level === "ROOT" || account?.level === "ADMIN";

  const client = useMemo(() => new AppAccountClient(http), [http]);

  const [secret, setSecret] = useState<{ value: string; username: string } | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [editingMethods, setEditingMethods] = useState<AppAccountDTO | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["app-account", "all"],
    queryFn: () => client.findAll(),
    enabled: allowed,
  });

  // Only fetched when the picker opens: several hundred descriptors that most
  // visits to this screen never need.
  const { data: methods, isLoading: methodsLoading } = useQuery({
    queryKey: ["app-account", "methods"],
    queryFn: () => client.discoverMethods(),
    enabled: allowed && editingMethods !== null,
    staleTime: 5 * 60_000,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["app-account", "all"] });

  const issue = useMutation({
    mutationFn: (username: string) =>
      client.issue({ username, status: "ACTIVE", method_codes: [] }),
    onSuccess: async (response) => {
      setIssuing(false);
      setNewUsername("");
      await refresh();
      setSecret({ value: response.plaintext_secret, username: response.account.username });
    },
    onError: (mutationError) =>
      toast.error(mutationError instanceof Error ? mutationError.message : t("error.generic", "Failed")),
  });

  const rotate = useMutation({
    mutationFn: (row: AppAccountDTO) => client.rotateKey(row.id),
    onSuccess: async (response) => {
      await refresh();
      setSecret({ value: response.plaintext_secret, username: response.account.username });
    },
    onError: (mutationError) =>
      toast.error(mutationError instanceof Error ? mutationError.message : t("error.generic", "Failed")),
  });

  const cols = useMemo(() => {
    const c = defineColumns<AppAccountDTO>();
    return [
      c.text("username", (row) => row.username, { headerKey: "column.username", priority: 1 }),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <Badge variant={row.status === "ACTIVE" ? "success" : "warning"} size="sm">
            {row.status}
          </Badge>
        ),
        { headerKey: "column.status", filter: "select", priority: 2 },
      ),
      c.number("methods", (row) => row.method_codes?.length ?? 0, {
        headerKey: "column.methods",
        priority: 3,
      }),
      c.text("creation_date", (row) => formatDate(row.creation_date), {
        headerKey: "column.created",
        priority: 4,
      }),
    ];
  }, []);

  const editor = useMemo<RowEditorConfig<AppAccountDTO>>(
    () => ({
      fields: {
        username: {
          kind: "text",
          get: (row) => row.username,
          set: (row, value) => ({ ...row, username: value }),
          required: true,
        },
        status: {
          kind: "select",
          get: (row) => row.status,
          set: (row, value) => ({ ...row, status: value as AppAccountDTO["status"] }),
          options: [
            { value: "ACTIVE", label: "ACTIVE" },
            { value: "SUSPENDED", label: "SUSPENDED" },
          ],
          required: true,
        },
      },
      validate: (row): Record<string, string> =>
        row.username.trim().length < 3
          ? { username: t("form.validation.field.invalid", "Invalid value") }
          : {},
      onSave: async (row) => {
        await client.update(row);
        await refresh();
        toast.success(t("notification.saved", "Saved"));
      },
      onDelete: async (row) => {
        await client.remove(row.id);
        await refresh();
        toast.success(t("notification.deleted", "Deleted"));
      },
      deleteConfirm: (row) => ({
        title: t("dialog.delete.app-account.title", "Delete this app account?"),
        message: t(
          "dialog.delete.app-account.message",
          "{{username}} will stop being able to call the API immediately.",
          { username: row.username },
        ),
      }),
      rowActions: (row) => (
        <>
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("app-account.methods.edit", "Edit allowed endpoints")}
            aria-label={t("app-account.methods.edit", "Edit allowed endpoints")}
            onClick={() => setEditingMethods(row)}
            icon={<ListChecks className="size-3.5" />}
          />
          <Button
            size="sm"
            variant="ghost"
            className="size-7 p-0"
            title={t("app-account.rotate", "Rotate key")}
            aria-label={t("app-account.rotate", "Rotate key")}
            onClick={async () => {
              const confirmed = await confirm({
                title: t("app-account.rotate.title", "Rotate this key?"),
                message: t(
                  "app-account.rotate.message",
                  "The current key stops working immediately. Anything using it must be updated.",
                ),
                variant: "danger",
              });
              if (confirmed) rotate.mutate(row);
            }}
            icon={<RefreshCw className="size-3.5" />}
          />
        </>
      ),
    }),
    [client, confirm, rotate, t, queryClient],
  );

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.app-accounts", "App accounts")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title={t("navigation.item.app-accounts", "App accounts")} />
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.app-accounts", "App accounts")}
        subtitle={t("app-account.subtitle", "Machine callers and their endpoint allowlists.")}
      />

      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        selectable
        editor={editor}
        actions={[
          {
            label: t("app-account.issue", "Issue key"),
            onClick: () => setIssuing(true),
            icon: <KeyRound className="size-4" />,
            variant: "primary",
            write: true,
          },
        ]}
        onDeleteSelected={async (rows) => {
          await client.removeBatch(rows.map((row) => row.id));
          await refresh();
          toast.success(t("notification.deleted", "Deleted"));
        }}
        emptyMessage={t("table.empty", "Nothing to show")}
      />

      {/* Issuing is a dialog rather than an inline add row: the response
          carries the plaintext key, which has to be surfaced immediately. */}
      <Dialog
        open={issuing}
        onClose={() => setIssuing(false)}
        title={t("app-account.issue.title", "Issue an app account key")}
        description={t(
          "app-account.issue.description",
          "Creates the account and generates its key. Choose which endpoints it may call afterwards.",
        )}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setIssuing(false)}>
              {t("button.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              loading={issue.isPending}
              disabled={newUsername.trim().length < 3}
              onClick={() => issue.mutate(newUsername.trim())}
            >
              {t("app-account.issue", "Issue key")}
            </Button>
          </>
        }
      >
        <Field label={t("column.username", "Username")} htmlFor="app-account-username" required>
          <Input
            id="app-account-username"
            value={newUsername}
            autoFocus
            onChange={(event) => setNewUsername(event.target.value)}
            placeholder="shopify-sync"
          />
        </Field>
      </Dialog>

      {editingMethods && (
        <MethodPickerDialog
          open
          onClose={() => setEditingMethods(null)}
          methods={methods ?? []}
          loading={methodsLoading}
          initial={editingMethods.method_codes ?? []}
          username={editingMethods.username}
          onSave={async (codes) => {
            await client.update({ ...editingMethods, method_codes: codes });
            await refresh();
            setEditingMethods(null);
            toast.success(t("notification.saved", "Saved"));
          }}
        />
      )}

      <SecretOnceDialog
        secret={secret?.value ?? null}
        username={secret?.username ?? ""}
        onClose={() => setSecret(null)}
      />
    </>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleDateString();
}
